import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import type { GridRowSelectionModel } from "@mui/x-data-grid";
import { useIsMutating } from "@tanstack/react-query";
import { type FC, lazy, type KeyboardEvent as ReactKeyboardEvent, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useCanModifyUsers } from "../../hooks/useCanModifyUsers";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useJobs } from "../../hooks/useJobs";
import { useIsMobile } from "../../hooks/useMobile";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { useRevealWage, useViewWageAllowed } from "../../hooks/useRevealWage";
import { DEACTIVATE_POST_SHIFTS_CHECKBOX_ID, DEACTIVATE_POST_SHIFTS_DEFAULT_BULK } from "../../pages/Profile/v2/GeneralDetails/useNurseFeatureData";
import { NEUTRAL, SHADOW, SURFACE } from "../../theme/tokens";
import type { Nurse } from "../../utils/models";
import { JumpingDots } from "../UIToolkit/Animations/JumpingDots";
import { FACET_SECTION_FLASH_MS } from "../UIToolkit/Filters/FacetFilterSection";
import { type SelectionAction, SelectionActionBar } from "../UIToolkit/molecules/SelectionActionBar";
import SystemDialog, { type DialogCheckboxDescriptor, type DialogCheckedState } from "../UIToolkit/Popups/Dialog";
import { isInsidePortalOverlay } from "../UIToolkit/Popups/portalOverlay";
import { AgencyWages } from "./AgencyWages/AgencyWages";
import { useAgencyConfigurationDeepLink } from "./AgencyWages/useAgencyConfigurationDeepLink";
import { BulkJobChangeDialog } from "./BulkJobChangeDialog";
import { CallInReasonsConfig } from "./CallInReasons/CallInReasonsConfig";
import { useCallInReasonsDeepLink } from "./CallInReasons/useCallInReasonsDeepLink";
import { MobileStaffList } from "./MobileStaffList";
import { useOpenStatusDecisions } from "./statusDecisions/hooks";
import { StatusDecisionsBanner } from "./statusDecisions/StatusDecisionsBanner";
import { useStatusDecisionsBanner } from "./statusDecisions/useStatusDecisionsBanner";
import { TeamAdmins } from "./TeamAdmins/TeamAdmins";
import { TeamEmptyState } from "./TeamEmptyState";
import { TeamFilterSidebar } from "./TeamFilterSidebar";
import { TeamHeader, type TeamTabItem, type TeamTabKey } from "./TeamHeader";
import { TeamSearchBar } from "./TeamSearchBar";
import { TeamStats } from "./TeamStats";
import { TeamTable } from "./TeamTable";
import { getColumnFilterGroup, TEAM_NAME_FIELD } from "./teamGridColumns";
import { BULK_NURSE_JOBS_APPLY_MUTATION_KEY } from "./useBulkNurseJobs";
import { useBulkNurseStatus } from "./useBulkNurseStatus";
import { useTeamFilters } from "./useTeamFilters";
import { isTeamWageSettingsRequested, parseTeamWageSettingsTarget, TEAM_WAGE_SETTINGS_PARAMS, type WageSettingsTarget } from "./WageSettings/deepLink";
import { WageSettings } from "./WageSettings/WageSettings";

// Support-only and seldom opened, so its grid, cells and the merge dialog stay out of the Team page's bundle.
const DuplicateEmployeesTab = lazy(() => import("./DuplicateEmployees/DuplicateEmployeesTab").then((module) => ({ default: module.DuplicateEmployeesTab })));

/** Identity of the bulk-only acknowledgement — the single-nurse dialog gates per-nurse instead. */
const BULK_DEACTIVATE_ACK_ID = "bulkDeactivate.ack";

/** Long enough that dragging across rows announces one total, short enough to feel immediate. */
const SELECTION_ANNOUNCE_DEBOUNCE_MS = 500;

export const Team: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { hospitalData, chosenDepartment, isMedicalDepartment, isHospitalView } = useHospitalData();
	const formatPersonName = useFormatPersonName();
	// Only hospital demos (Hospital type + inhouse2).
	const isRealHospitalDemo = isHospitalView;
	const { isMobile } = useIsMobile();
	const { userInfo } = useAuth();
	const userId = userInfo?.id;
	const canViewWage = useRevealWage();
	// The permission alone. `useRevealWage()` also folds in the localStorage privacy toggle, which must
	// not decide whether a server-persisting editing surface exists (see that hook's own contract).
	const canEditWageSettings = useViewWageAllowed();

	// All filter state / persistence / derived rows + counts live in one hook so this page stays thin.
	const tf = useTeamFilters();

	const [activeTabKey, setActiveTabKey] = useState<TeamTabKey>("team");
	// Where a `?wage_settings=true&section=…&rule=…` link asked Wage Settings to land. Held with the
	// department it was read for: the link names a rule of THAT department, so it must not scroll and
	// highlight in another one the user switches to afterwards.
	const [wageSettingsTarget, setWageSettingsTarget] = useState<{ departmentId: string; target: WageSettingsTarget } | undefined>(undefined);
	const handleTabChange = useCallback((key: TeamTabKey) => {
		// A tab the user picked is a fresh visit, so it opens at the top rather than re-landing the old link.
		setWageSettingsTarget(undefined);
		setActiveTabKey(key);
	}, []);

	// The banner's dismiss UI stays here; react-query dedupes the shared open-decisions fetch with the hook.
	const { data: openDecisions } = useOpenStatusDecisions(chosenDepartment.department_id);
	const { count: decisionsCount, isBannerVisible, dismiss: dismissBanner } = useStatusDecisionsBanner(openDecisions, chosenDepartment.department_id, userId);

	// DAT-561's ?needs_decision=true deep link needs no special handling any more: every filter now
	// lives in the URL, so that parameter IS the decisions view rather than a one-shot instruction to
	// apply it and then strip itself.
	//
	// `?wage_settings=true` still needs one: it selects a TAB, which is local state rather than
	// URL-backed filter state. Stripped once applied so Back doesn't re-select over the user's choice —
	// with its section and row params, which only mean something alongside it.
	const [searchParams, setSearchParams] = useSearchParams();
	useEffect(() => {
		if (!isTeamWageSettingsRequested(searchParams)) return;
		setActiveTabKey("wageSettings");
		const target = parseTeamWageSettingsTarget(searchParams);
		setWageSettingsTarget(target && { departmentId: chosenDepartment.department_id, target });
		// Updater form, not the snapshot: `useTeamFilters` writes the same query string, so stripping
		// from a snapshot could revert a filter changed in the same tick.
		setSearchParams(
			(current) => {
				const remaining = new URLSearchParams(current);
				for (const param of TEAM_WAGE_SETTINGS_PARAMS) remaining.delete(param);
				return remaining;
			},
			{ replace: true },
		);
	}, [searchParams, setSearchParams, chosenDepartment.department_id]);

	// DAT-906 · `?agency_configuration=true` (an agency shift's wage breakdown) selects the Agency
	// Configuration tab the same way, stripped once applied.
	const openAgencyTab = useCallback(() => {
		setWageSettingsTarget(undefined);
		setActiveTabKey("agency");
	}, []);
	useAgencyConfigurationDeepLink(openAgencyTab);

	// DAT-850 · `?call_in_reasons=true` (the Hiring page's absence panel) selects the Call-in reasons tab.
	const openCallInReasonsTab = useCallback(() => {
		setWageSettingsTarget(undefined);
		setActiveTabKey("callInReasons");
	}, []);
	useCallInReasonsDeepLink(openCallInReasonsTab);

	const handleAddStaff = useCallback(() => {
		navigate(`/${chosenDepartment.department_id}/add-nurse`);
	}, [chosenDepartment.department_id, navigate]);

	// ── Bulk selection (mass activate/deactivate) ──
	const { activateNurses, deactivateNurses, isBulkActivating, isBulkDeactivating } = useBulkNurseStatus();
	// These configuration tabs, unlike employee-status actions, require `modify_user`.
	const canModifyUsers = useCanModifyUsers();
	// No "selection mode" to enter: checkboxes appear on row hover, so the bulk bar simply follows
	// whether anything is selected.
	const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({ type: "include", ids: new Set() });
	const [bulkAction, setBulkAction] = useState<"activate" | "deactivate" | "jobs" | null>(null);
	// Read by key rather than owned here: the jobs dialog runs its own mutation, and the page only needs
	// to know a batch is on the wire so nothing (Escape, a second click) can act on the selection under it.
	const isBulkChangingJobs = useIsMutating({ mutationKey: BULK_NURSE_JOBS_APPLY_MUTATION_KEY }) > 0;
	// Nothing to add or replace with when the department offers no jobs, so the action isn't offered.
	const hasEnabledJobs = useJobs().length > 0;

	// TeamTable configures the grid with `disableRowSelectionExcludeModel`, so the model is always in
	// `include` mode — select-all materialises every filtered id rather than an "all but these" set.
	const selectedNurses = useMemo<Nurse[]>(() => {
		const { ids } = rowSelectionModel;
		return tf.nurses.filter((n) => ids.has(n.id));
	}, [tf.nurses, rowSelectionModel]);

	const resetSelection = useCallback(() => setRowSelectionModel({ type: "include", ids: new Set() }), []);

	/**
	 * What Export writes: the selection when there is one, otherwise every row the filters and search
	 * currently leave visible. The endpoint used to take no roster at all and always wrote the
	 * department's ACTIVE employees, so exporting a filtered view silently produced something else —
	 * an inactive-only view exported that view's exact complement.
	 */
	// The export must mirror what the user is looking at, and the two viewports look at different
	// rosters: mobile renders `nursesIgnoringStatFilter`, so sending `tf.nurses` there produced a
	// workbook missing rows that were on screen whenever a stat segment was active.
	const exportNurseIds = useMemo(() => {
		const visibleRoster = isMobile ? tf.nursesIgnoringStatFilter : tf.nurses;
		return (!isMobile && selectedNurses.length > 0 ? selectedNurses : visibleRoster).map((n) => n.id);
	}, [selectedNurses, tf.nurses, tf.nursesIgnoringStatFilter, isMobile]);

	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const selectedCount = selectedNurses.length;

	/**
	 * Clearing the selection unmounts the SelectionActionBar button MUI's Dialog restores focus to,
	 * so focus lands on `<body>` — after a batch, after Cancel, after Escape, and after MUI itself
	 * drops the model because the last selected row left the filtered set. One effect on the
	 * "something selected → nothing selected" transition covers all four; `requestAnimationFrame`
	 * puts it after MUI's own restore attempt rather than racing it.
	 */
	const hadSelectionRef = useRef(false);
	useEffect(() => {
		const hadSelection = hadSelectionRef.current;
		hadSelectionRef.current = selectedCount > 0;
		if (!hadSelection || selectedCount > 0) return;

		const frame = requestAnimationFrame(() => {
			if (document.activeElement === document.body) gridWrapperRef.current?.focus();
		});
		return () => cancelAnimationFrame(frame);
	}, [selectedCount]);

	// Debounced so dragging through a dozen rows announces the total once, not twelve times.
	const announcedSelectedCount = useDebouncedValue(selectedCount, SELECTION_ANNOUNCE_DEBOUNCE_MS);

	/**
	 * Escape clears the selection — but never out from under one of our own dialogs or an in-flight
	 * batch. MUI's Modal and Menu stop Escape from propagating, so a row menu or a confirm dialog
	 * never reaches this handler in the first place; the guards below are for the cases that do.
	 *
	 * Deliberately NOT a `document.querySelector('[role="dialog"], …')` sweep: `FloatingChatWindow`
	 * is a non-modal `role="dialog"` that floats over every page, so a global role query silently
	 * disables Escape here whenever the chat window happens to be open.
	 */
	const handleGridKeyDown = useCallback(
		(event: ReactKeyboardEvent<HTMLDivElement>) => {
			if (event.key !== "Escape" || event.defaultPrevented) return;
			if (selectedCount === 0) return;
			// Escape must never orphan a mutation that is already on the wire, nor answer a confirm
			// dialog that is asking about this very selection.
			if (bulkAction !== null || isBulkActivating || isBulkDeactivating || isBulkChangingJobs) return;
			if (isInsidePortalOverlay(event.target)) return;
			event.preventDefault();
			resetSelection();
		},
		[selectedCount, bulkAction, isBulkActivating, isBulkDeactivating, isBulkChangingJobs, resetSelection],
	);

	// Switching departments must never leave a stale selection that a bulk action could target.
	// department_id is an intentional trigger dependency (the effect resets on department change),
	// even though the effect body doesn't read it.
	// biome-ignore lint/correctness/useExhaustiveDependencies: department_id is the reset trigger.
	useEffect(() => {
		resetSelection();
	}, [chosenDepartment.department_id, resetSelection]);

	// The mobile roster has no row-selection UI. Drop desktop-only selection when entering mobile so
	// an invisible selection cannot drive the bulk bar or a later export.
	useEffect(() => {
		if (isMobile) {
			resetSelection();
		}
	}, [isMobile, resetSelection]);

	const handleConfirmActivate = useCallback(async () => {
		setBulkAction(null);
		await activateNurses(selectedNurses);
		resetSelection();
	}, [activateNurses, selectedNurses, resetSelection]);

	const handleConfirmDeactivate = useCallback(
		async (checkedState?: DialogCheckedState[]) => {
			// Fails closed: posting the freed shifts is an opt-in side effect, so a missing state means
			// "don't post", never "post anyway".
			const postShifts = checkedState?.find((c) => c.id === DEACTIVATE_POST_SHIFTS_CHECKBOX_ID)?.checked === true;
			setBulkAction(null);
			await deactivateNurses(selectedNurses, postShifts);
			resetSelection();
		},
		[deactivateNurses, selectedNurses, resetSelection],
	);

	const handleCloseBulkAction = useCallback(() => setBulkAction(null), []);
	// Whoever couldn't be updated stays selected, so retrying is one click rather than a hunt through the roster.
	const handleJobsApplied = useCallback(
		(failedNurseIds: number[]) => {
			setBulkAction(null);
			if (failedNurseIds.length === 0) {
				resetSelection();
				return;
			}
			// Only re-select failures still on screen: an id outside the filtered roster would linger invisibly
			// in the model and resurface as a selection when the filters change.
			const visibleIds = new Set(tf.nurses.map((n) => n.id));
			setRowSelectionModel({ type: "include", ids: new Set(failedNurseIds.filter((id) => visibleIds.has(id))) });
		},
		[resetSelection, tf.nurses],
	);

	const bulkActions = useMemo<SelectionAction[]>(
		() => [
			...(hasEnabledJobs
				? [
						{
							id: "change-job",
							label: t("team.bulk.jobs.action"),
							onClick: () => setBulkAction("jobs"),
							leadingIcon: <WorkHistoryOutlinedIcon sx={{ fontSize: 18 }} />,
							loading: isBulkChangingJobs,
						},
					]
				: []),
			{
				id: "activate",
				label: t("team.bulk.activate"),
				onClick: () => setBulkAction("activate"),
				leadingIcon: <HowToRegOutlinedIcon sx={{ fontSize: 18 }} />,
				loading: isBulkActivating,
			},
			{
				id: "deactivate",
				label: t("team.bulk.deactivate"),
				onClick: () => setBulkAction("deactivate"),
				leadingIcon: <PersonOffOutlinedIcon sx={{ fontSize: 18 }} />,
				buttonVariant: "delete",
				loading: isBulkDeactivating,
			},
		],
		[t, isBulkActivating, isBulkDeactivating, hasEnabledJobs, isBulkChangingJobs],
	);

	// `t` is referentially stable, so this array's identity is too — which matters, because
	// SystemDialog rebuilds (and therefore un-ticks) its checked state whenever the array changes.
	// That is also why the acknowledgement is worded for "the selection" rather than "{{count}} people".
	const bulkDeactivateCheckboxes = useMemo<DialogCheckboxDescriptor[]>(
		() => [
			{
				id: BULK_DEACTIVATE_ACK_ID,
				label: t("team.bulk.deactivate.ack"),
				required: true,
				defaultValue: false,
			},
			{
				id: DEACTIVATE_POST_SHIFTS_CHECKBOX_ID,
				label: t("team.deactivate.post_shifts.label"),
				helperText: t("team.deactivate.post_shifts.helper"),
				required: false,
				// Off for a batch, on for a single employee — see the constants' own doc for why the two
				// differ, which is deliberate rather than a drift between these files.
				defaultValue: DEACTIVATE_POST_SHIFTS_DEFAULT_BULK,
			},
		],
		[t],
	);

	const renderBulkBody = useCallback(
		(intro: string) => (
			<Box>
				<Box sx={{ mb: 1 }}>{intro}</Box>
				<Box component="ul" sx={{ m: 0, pl: 2.5, maxHeight: 200, overflowY: "auto" }}>
					{selectedNurses.map((n) => (
						<li key={n.id}>{formatPersonName(n)}</li>
					))}
				</Box>
			</Box>
		),
		[selectedNurses, formatPersonName],
	);

	// Collapse is session-only on purpose. APP-609 asks the page to always come back to its default
	// state, so remembering a collapsed rail across visits would reintroduce exactly the "why is this
	// page set up like this?" problem the ticket is about.
	const [filtersCollapsed, setFiltersCollapsed] = useState(false);

	/**
	 * A column's "Filter by…" hands the user to the rail group that filters the same field.
	 *
	 * Without the map the grid would have to grow its own per-column filter UI competing with the
	 * rail; with it, the column menu is a shortcut INTO the one filtering surface the page has.
	 * Columns absent from the map get no "Filter by…" item at all.
	 */
	const [focusedGroup, setFocusedGroup] = useState<string | null>(null);
	const focusTimer = useRef<number | null>(null);
	const searchInputRef = useRef<HTMLInputElement | null>(null);
	const searchFocusFrame = useRef<number | null>(null);

	const canFilterByColumn = useCallback((field: string) => field === TEAM_NAME_FIELD || getColumnFilterGroup(field) !== undefined, []);

	const filterByHint = useCallback(
		(field: string) => {
			if (field === TEAM_NAME_FIELD) return t("team.filter_by.hint.search");
			if (getColumnFilterGroup(field)) return t("team.filter_by.hint.rail");
			return undefined;
		},
		[t],
	);

	const handleFilterByColumn = useCallback((field: string) => {
		// Name is filtered by searching, so its hand-off is the caret in the search field rather than a
		// rail group. Deferred a frame because the grid's column menu refocuses whatever was focused when
		// it opened (GridMenu's layout effect on close) — focusing here and now just loses to that.
		if (field === TEAM_NAME_FIELD) {
			if (searchFocusFrame.current) window.cancelAnimationFrame(searchFocusFrame.current);
			searchFocusFrame.current = window.requestAnimationFrame(() => {
				searchInputRef.current?.focus();
				// Select rather than append: the menu was opened to start a new lookup, not extend one.
				searchInputRef.current?.select();
			});
			return;
		}
		const group = getColumnFilterGroup(field);
		if (!group) return;
		// Reopening the rail is the point of the hand-off — a jump into a collapsed rail lands nowhere.
		setFiltersCollapsed(false);
		setFocusedGroup(group);
		if (focusTimer.current) window.clearTimeout(focusTimer.current);
		// Released so the flash can fire again the next time the same group is jumped to.
		focusTimer.current = window.setTimeout(() => setFocusedGroup(null), FACET_SECTION_FLASH_MS);
	}, []);

	useEffect(
		() => () => {
			if (focusTimer.current) window.clearTimeout(focusTimer.current);
			if (searchFocusFrame.current) window.cancelAnimationFrame(searchFocusFrame.current);
		},
		[],
	);

	const filterSidebarNode = (
		<TeamFilterSidebar
			activeFilterCount={tf.activeFilterCount}
			onClearAll={tf.clearAllFilters}
			facetCounts={tf.facetCounts}
			collapsed={filtersCollapsed}
			onCollapsedChange={setFiltersCollapsed}
			focusedGroup={focusedGroup}
			selectedStatuses={tf.selectedStatuses}
			onStatusesChange={tf.onStatusesChange}
			selectedAgencies={tf.selectedAgencies}
			onAgenciesChange={tf.onAgenciesChange}
			selectedUnions={tf.selectedUnions}
			onUnionsChange={tf.onUnionsChange}
			selectedLastLogin={tf.selectedLastLogin}
			onLastLoginChange={tf.onLastLoginChange}
			showOnlyDecisions={tf.showOnlyDecisions}
			onShowOnlyDecisionsChange={tf.onShowOnlyDecisionsChange}
			decisionsCount={tf.decisionsCount}
			selectedRoles={tf.selectedRoles}
			onRolesChange={tf.onRolesChange}
			selectedJobNames={tf.selectedJobNames}
			onJobsChange={tf.onJobsChange}
			selectedSections={tf.selectedSections}
			onSectionsChange={tf.onSectionsChange}
		/>
	);

	const headerRef = useRef<HTMLDivElement>(null);
	const [headerHeight, setHeaderHeight] = useState<number>(0);
	useLayoutEffect(() => {
		const updateHeight = () => {
			if (headerRef.current) {
				setHeaderHeight(headerRef.current.clientHeight);
			}
		};

		updateHeight();
		window.addEventListener("resize", updateHeight);
		return () => window.removeEventListener("resize", updateHeight);
	}, []);

	const showAgencyTab = !!(userInfo && canViewWage && !isMedicalDepartment);
	// No `!isMedicalDepartment` clause, unlike the agency tab: these wages apply there too.
	const showWageSettingsTab = !!(userInfo && canEditWageSettings);
	const showAdminsTab = canModifyUsers;
	const showCallInReasonsTab = canModifyUsers;
	const showDuplicatesTab = !!userInfo?.support;

	const tabs = useMemo<TeamTabItem[]>(() => {
		const arr: TeamTabItem[] = [{ key: "team", label: t("Team") }];
		if (showAgencyTab) arr.push({ key: "agency", label: t("agency.configuration") });
		if (showWageSettingsTab) arr.push({ key: "wageSettings", label: t("team.tab.wageSettings") });
		if (showAdminsTab) arr.push({ key: "admins", label: t("team.tab.users") });
		if (showCallInReasonsTab) arr.push({ key: "callInReasons", label: t("call.in.reasons.title") });
		if (showDuplicatesTab) arr.push({ key: "duplicates", label: t("team.tab.duplicates") });
		return arr;
	}, [t, showAgencyTab, showWageSettingsTab, showAdminsTab, showCallInReasonsTab, showDuplicatesTab]);

	// If permissions change and the active tab is no longer available, fall back to "team".
	const activeTabExists = tabs.some((tab) => tab.key === activeTabKey);
	const effectiveTabKey: TeamTabKey = activeTabExists ? activeTabKey : "team";

	return (
		<Box
			data-testid="team-root"
			data-hospital-demo={isRealHospitalDemo ? "true" : undefined}
			sx={(theme) => ({
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				backgroundColor: alpha(theme.palette.primary.main, 0.025),
			})}
		>
			<Box ref={headerRef}>
				<TeamHeader
					tabs={tabs}
					activeTabKey={effectiveTabKey}
					onTabChange={handleTabChange}
					exportNurseIds={exportNurseIds}
					statusDecisionsBanner={
						isBannerVisible ? <StatusDecisionsBanner count={decisionsCount} onViewAndDecide={tf.handleViewAndDecide} onDismiss={dismissBanner} /> : undefined
					}
				/>
			</Box>
			<Box
				sx={{
					height: `calc(100% - ${headerHeight}px)`,
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
				}}
			>
				{isMobile ? (
					// The faceted rail is web-only, and mobile has no control to see or clear a filter — which
					// is why filter state no longer persists at all: a rail narrowed on desktop used to
					// silently narrow this list too, with "No nurses found." as the only explanation.
					<MobileStaffList nurses={tf.nursesIgnoringStatFilter} />
				) : (
					<Box
						sx={{
							p: 1,
							flexGrow: 1,
							minHeight: 0,
							display: "flex",
							flexDirection: "column",
							// The grid owns its own horizontal scroll, so this container must not add a second
							// one: `overflow: auto` here produced a page-level sideways scroll of ~23px that
							// revealed nothing and clipped the right edge to do it.
							overflowY: "auto",
							overflowX: "hidden",
						}}
					>
						{effectiveTabKey === "team" && (
							<Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: 1 }}>
								<TeamStats
									stats={tf.teamStats}
									activeStatFilter={tf.activeStatFilter}
									onCycleStatFilter={tf.cycleStatFilter}
									onClearStatFilter={tf.clearStatFilter}
									onResetAll={tf.clearAllFilters}
									// NOTE: always false today. `HospitalDataProvider` blocks its children behind a
									// full-page loader until the roster exists, so by the time this page renders there
									// is nothing left to wait for. `SystemDataGrid` and the tiles both support
									// skeletons now; using them needs that provider-level gate lifted first, which is
									// an app-shell change affecting every page.
									loading={false}
								/>
								{/*
								 * The rail and the table are ONE framed surface — no gap between them.
								 * They were two separately-bordered cards with 8px of page showing through, which
								 * reads as two unrelated panels when the whole point of the rail is that it drives
								 * the table beside it. The frame lives here; the rail contributes the divider
								 * (`frame="flush"`) and the grid drops its own outer border.
								 */}
								<Box
									sx={{
										display: "flex",
										flexDirection: "row",
										flex: 1,
										minHeight: 0,
										alignItems: "stretch",
										backgroundColor: SURFACE,
										border: `1px solid ${NEUTRAL[200]}`,
										borderRadius: "8px",
										boxShadow: SHADOW.PANEL_SOFT,
										overflow: "hidden",
									}}
								>
									{filterSidebarNode}
									{/* minWidth 0 keeps the DataGrid's own horizontal scroll instead of blowing out the row. */}
									{/* This wrapper is both the Escape shortcut's scope and the focus target for a cleared
									    selection that would otherwise drop focus onto <body>. */}
									<Box
										ref={gridWrapperRef}
										tabIndex={-1}
										onKeyDown={handleGridKeyDown}
										sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0, outline: "none" }}
									>
										{/*
										 * Always mounted, and outside the `selectedCount > 0` gate: a live region that
										 * mounts with its content is not announced, and "0 selected" on clear would never
										 * be spoken at all. Batch completion is deliberately NOT announced here —
										 * CustomSnackbar wraps MUI Alert, whose implicit role="alert" already does.
										 */}
										<Box role="status" aria-live="polite" sx={visuallyHidden}>
											{announcedSelectedCount > 0 ? t("team.bulk.selected_count", { count: announcedSelectedCount }) : t("team.bulk.selection_cleared")}
										</Box>
										{selectedCount > 0 && (
											<Box sx={{ p: 1, pb: 0 }}>
												<SelectionActionBar
													selectedCount={selectedCount}
													countLabel={t("team.bulk.selected_count", { count: selectedCount })}
													ariaLabel={t("team.bulk.toolbar.label")}
													actions={bulkActions}
													onCancel={resetSelection}
													dataTestId="team-selection-bar"
												/>
											</Box>
										)}
										<TeamTable
											nurses={tf.nurses}
											loading={tf.rosterPending}
											decisionsByNurseId={tf.decisionsByNurseId}
											canFilterByColumn={canFilterByColumn}
											onFilterByColumn={handleFilterByColumn}
											filterByHint={filterByHint}
											rowSelectionModel={rowSelectionModel}
											onRowSelectionModelChange={setRowSelectionModel}
											filtersNode={
												<TeamSearchBar
													value={tf.searchQuery}
													onChange={tf.onSearchChange}
													shownCount={tf.nurses.length}
													totalCount={tf.totalNurseCount}
													inputRef={searchInputRef}
												/>
											}
											emptyState={
												<TeamEmptyState
													hasFiltersApplied={tf.hasFiltersApplied}
													totalStaffCount={hospitalData.nurses.length}
													onClearFilters={tf.clearAllFilters}
													onAddStaff={handleAddStaff}
												/>
											}
										/>
									</Box>
								</Box>
							</Box>
						)}
						{effectiveTabKey === "agency" && showAgencyTab && <AgencyWages />}
						{effectiveTabKey === "wageSettings" && showWageSettingsTab && (
							<WageSettings
								key={chosenDepartment.department_id}
								target={wageSettingsTarget?.departmentId === chosenDepartment.department_id ? wageSettingsTarget.target : undefined}
							/>
						)}
						{effectiveTabKey === "admins" && showAdminsTab && <TeamAdmins />}
						{effectiveTabKey === "callInReasons" && showCallInReasonsTab && <CallInReasonsConfig />}
						{effectiveTabKey === "duplicates" && showDuplicatesTab && (
							<Suspense
								fallback={
									<Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
										<JumpingDots size={6} />
									</Box>
								}
							>
								<DuplicateEmployeesTab key={chosenDepartment.department_id} />
							</Suspense>
						)}
					</Box>
				)}
			</Box>

			{bulkAction === "activate" && (
				<SystemDialog
					open
					onClose={() => setBulkAction(null)}
					title={t("team.bulk.activate.title")}
					body={renderBulkBody(t("team.bulk.activate.body", { count: selectedCount }))}
					onConfirm={handleConfirmActivate}
					confirm={t("team.bulk.activate.confirm")}
					confirmLoading={isBulkActivating}
					dataTestId="team-bulk-activate-dialog"
				/>
			)}

			{bulkAction === "deactivate" && (
				<SystemDialog
					open
					onClose={() => setBulkAction(null)}
					title={t("team.bulk.deactivate.title")}
					body={renderBulkBody(t("team.bulk.deactivate.body", { count: selectedCount }))}
					onConfirm={handleConfirmDeactivate}
					confirm={t("team.bulk.deactivate.confirm")}
					confirmButtonVariant="delete"
					checkboxes={bulkDeactivateCheckboxes}
					confirmLoading={isBulkDeactivating}
					dataTestId="team-bulk-deactivate-dialog"
				/>
			)}

			{bulkAction === "jobs" && <BulkJobChangeDialog nurses={selectedNurses} onClose={handleCloseBulkAction} onApplied={handleJobsApplied} />}
		</Box>
	);
};
